'use client';

/**
 * ActivityMobileCard Component
 *
 * A reusable mobile card component for displaying activity information
 * with image overlay, title, professional name, and pricing details.
 * Used in drawers, modals, and other mobile interfaces.
 * Can also display booking-specific information like date, time, and timeAgo badge.
 */
import { ChevronRight, Clock, Home, MapPin, Video } from 'lucide-react';
import React from 'react';

import { formatCurrency } from '../../../../lib/utils';
import { ImageWithFallback } from '../../../common/imageWithFallback/ImageWithFallback';
import { Badge } from '../../../ui/badge';
import { type LocationKind } from '../locationKindChips';

// Default labels object to avoid unstable default prop
const DEFAULT_LABELS: ActivityMobileCardProps['labels'] = {
  atHomeBadge: 'At Home',
  priceLabel: 'Price',
  priceFrom: 'from',
  contactForPrice: 'Contact for price'
};

export interface ActivityMobileCardProps {
  /**
   * Activity title
   */
  title: string;

  /**
   * Professional/instructor name
   */
  professionalName: string;

  /**
   * Activity image URL
   */
  imageUrl?: string;

  /**
   * Whether the activity is conducted at home
   */
  isAtHome?: boolean;

  /**
   * Whether this booking is online (visio). Shows a violet video icon.
   */
  isOnline?: boolean;

  /**
   * The activity's real delivery-mode MIX (venue / at_home / online). When it
   * holds 2+ kinds the overlay renders one compact chip per kind instead of the
   * single `isAtHome`/`isOnline` indicator. Single-kind (or absent) keeps the
   * legacy single overlay. Used by the activity drawer (not booking cards).
   */
  locationKinds?: LocationKind[];

  /** Chip labels for the location-kind mix; required to render the chips. */
  locationKindLabels?: { venue: string; at_home: string; online: string };

  /**
   * Price information (number in EUR or a pre-formatted string)
   */
  startingPrice?: number | string | null;

  /**
   * Locale for currency formatting
   * @default 'fr-FR'
   */
  locale?: string;

  /**
   * Custom height for the card
   * @default "h-[250px]"
   */
  height?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Text translations for labels
   */
  labels?: {
    atHomeBadge?: string;
    priceLabel?: string;
    priceFrom?: string;
    contactForPrice?: string;
    free?: string;
  };

  /**
   * Custom fallback image to use when imageUrl is not provided
   */
  fallbackImage?: string;

  /**
   * Click handler for the entire card
   */
  onClick?: () => void;

  /**
   * When set, the title renders as a link to this href (e.g. the activity page).
   */
  titleHref?: string;

  /**
   * Click handler fired alongside following `titleHref` (e.g. to close a dialog).
   */
  onTitleClick?: () => void;

  // Booking-specific props
  /**
   * Date information for bookings
   */
  date?: string;

  /**
   * Time information for bookings
   */
  time?: string;

  /**
   * Time ago information for bookings
   */
  timeAgo?: string;

  /**
   * Whether this booking is upcoming (affects timeAgo badge styling)
   * @default true
   */
  isUpcoming?: boolean;

  /**
   * Whether this booking is currently ongoing (started but not ended)
   * Used for styling the timeAgo badge in blue
   * @default false
   */
  isOngoing?: boolean;

  /**
   * Whether this card is displayed on a booking page
   * If false, shows a "PRICE" title above the price
   * @default false
   */
  isFromBookingPage?: boolean;

  /**
   * Whether to hide the professional name
   * Used in contexts where only the activity title is needed
   * @default false
   */
  hideProfessionalName?: boolean;

  /**
   * Whether to show "from" text before the price
   * @default true
   */
  showPriceFrom?: boolean;

  /**
   * Hide the price block entirely. Use when the card represents something
   * without a single meaningful price (e.g. an aggregate group booking),
   * so it doesn't fall back to the "contact for price" label.
   * @default false
   */
  hidePrice?: boolean;

  /**
   * Optional badge element to display (e.g., payment status)
   */
  badge?: React.ReactNode;

  /**
   * Handler for Pay Now action (shown when payment is pending)
   */
  onPayNow?: () => void;

  /**
   * Text for the Pay Now button
   * @default "Pay Now"
   */
  payNowText?: string;
}

/**
 * ActivityMobileCard component for displaying activity information in mobile interfaces
 *
 * @example
 * ```tsx
 * <ActivityMobileCard
 *   title="DDP Spiritual Yoga"
 *   professionalName="Marie Dupont"
 *   imageUrl="/path/to/image.jpg"
 *   isAtHome={true}
 *   startingPrice={4000}
 *   labels={{
 *     atHomeBadge: "At Home",
 *     priceLabel: "PRICE",
 *     priceFrom: "from",
 *     contactForPrice: "Contact for price"
 *   }}
 * />
 * ```
 */
export const ActivityMobileCard: React.FC<ActivityMobileCardProps> = ({
  title,
  professionalName,
  imageUrl,
  isAtHome = false,
  isOnline = false,
  locationKinds,
  locationKindLabels,
  startingPrice,
  height = 'h-[260px]', // Match MobileBookingCard height
  className = '',
  labels = DEFAULT_LABELS,
  fallbackImage,
  onClick,
  titleHref,
  onTitleClick,
  // Booking-specific props
  date,
  time,
  timeAgo,
  isUpcoming = true,
  isOngoing = false,
  isFromBookingPage = false,
  hideProfessionalName = false,
  showPriceFrom = true,
  hidePrice = false,
  badge,
  onPayNow,
  payNowText = 'Pay Now',
  locale = 'fr-FR'
}) => {
  return (
    <div
      className={`relative ${height} flex rounded-xl overflow-hidden group ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <ImageWithFallback
          src={imageUrl || fallbackImage}
          alt={title}
          className="w-full h-full object-cover rounded-xl"
          sizes="100vw"
        />
        {/* Scrim: a light overall dim plus a stronger bottom gradient so the
            overlaid title / date / price stay legible over bright photos. */}
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
      </div>

      {/* TimeAgo badge - top left */}
      {timeAgo && (
        <div className="absolute top-3 left-3">
          <Badge
            className={`backdrop-blur-sm border-0 text-xs px-2 py-1 flex items-center gap-1 rounded-lg pointer-events-none ${
              isOngoing
                ? 'bg-info-100/90 text-info-700'
                : isUpcoming
                  ? 'bg-success-100/90 text-success-700'
                  : 'bg-neutral-100/90 text-neutral-600'
            }`}
          >
            <Clock
              className={`w-3 h-3 ${
                isOngoing ? 'text-info-600' : isUpcoming ? 'text-success-600' : 'text-neutral-500'
              }`}
            />
            {timeAgo}
          </Badge>
        </div>
      )}

      {/* Payment status badge - bottom right (above price) */}
      {badge && <div className="absolute bottom-16 right-3">{badge}</div>}

      {/* Content overlay */}
      <div className="absolute bottom-3 left-3 text-white">
        {/* Location indicator: chips for a mixed activity, single row otherwise.
            On the dark photo overlay everything stays white for legibility. */}
        {(locationKinds?.length ?? 0) >= 2 && locationKindLabels ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            {(['venue', 'at_home', 'online'] as const)
              .filter((k) => locationKinds?.includes(k))
              .map((k) => {
                const Icon = k === 'online' ? Video : k === 'at_home' ? Home : MapPin;
                return (
                  <span key={k} className="inline-flex items-center gap-1">
                    <Icon className="w-3 h-3" aria-hidden />
                    <span>{locationKindLabels[k]}</span>
                  </span>
                );
              })}
          </div>
        ) : isOnline ? (
          <div className="flex items-center gap-1 text-xs">
            <Video className="w-3 h-3" />
            <span>{labels.atHomeBadge}</span>
          </div>
        ) : isAtHome ? (
          <div className="flex items-center gap-1 text-xs">
            <Home className="w-3 h-3" />
            <span>{labels.atHomeBadge}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs">
            <MapPin className="w-3 h-3" />
            <span>{labels.atHomeBadge || 'At Venue'}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {' '}
          <h3 className="font-semibold text-lg text-neutral-0 mb-1 max-w-[80vw] line-clamp-2">
            {titleHref ? (
              <a
                href={titleHref}
                onClick={onTitleClick}
                className="transition-colors hover:text-primary-200 hover:underline focus:underline focus:outline-none"
              >
                {title}
              </a>
            ) : (
              title
            )}
          </h3>
          {!isFromBookingPage && (
            <ChevronRight className="text-white transition-transform duration-200 group-hover:translate-x-1 group-hover:text-primary-200" />
          )}
        </div>
        {!hideProfessionalName && <p className="text-sm text-neutral-200 mb-1 font-regular">{professionalName}</p>}
        {date && (
          <span className="text-sm text-neutral-200 text-nowrap truncate mb-1 font-regular">
            {date} • {time}
          </span>
        )}
      </div>

      {/* Price or Pay Now button */}
      {!hidePrice && (
        <div className="absolute bottom-3 flex flex-col items-end right-3 text-neutral-200">
          {onPayNow ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPayNow();
              }}
              className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
            >
              {payNowText}
            </button>
          ) : (
            <>
              <p className="mb-1">{labels.priceLabel}</p>
              <div className="flex items-center gap-1 text-neutral-200">
                {typeof startingPrice === 'number' && startingPrice === 0 ? (
                  <p className=" text-lg leading-none">{labels.free ?? 'Free'}</p>
                ) : (
                  <>
                    {showPriceFrom && <p className="font-regular ">{labels.priceFrom}</p>}
                    <p className=" text-lg leading-none">
                      {startingPrice
                        ? typeof startingPrice === 'number'
                          ? formatCurrency(startingPrice, 'EUR', locale)
                          : startingPrice
                        : labels.contactForPrice}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
